import type { JSX } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { Calendar, CheckCircle2, User, XCircle } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import type {
  LeaveLifecycleStepDto,
  LeaveRequestDto,
} from '@/utils/api/leave-service/models'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/features/authentication/components/auth-context'
import {
  getSupervisorApprovedRequestsQueryKey,
  getSupervisorPendingRequestsQueryKey,
  getTrackLeaveApplicationQueryKey,
  useApproveOrReject,
  useTrackLeaveApplication,
} from '@/utils/api/leave-service'
import { formatDate } from '@/utils/DateHelpers'

interface LeaveDetailsDrawerProps {
  leaveRequestId: number | null
  leaveRequest?: LeaveRequestDto | null
  open: boolean
  onClose: () => void
  onActionComplete?: () => void
}

function formatDaysValue(value?: number) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '—'
  }
  return `${new Intl.NumberFormat().format(value)} days`
}

function formatOptionalDate(value?: string) {
  if (!value) {
    return '—'
  }
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? '—' : formatDate(parsed)
}

export function LeaveDetailsDrawer({ leaveRequestId, leaveRequest, open, onClose, onActionComplete }: LeaveDetailsDrawerProps) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [actionNote, setActionNote] = useState('')

  // Fetch leave tracking/approval chain (contains all necessary info)
  const trackingQuery = useTrackLeaveApplication(leaveRequestId!, {
    query: { enabled: !!leaveRequestId },
  })

  // Approval/rejection mutation
  const approveOrRejectMutation = useApproveOrReject({
    mutation: {
      onSuccess: () => {
        toast.success(actionNote ? 'Action completed successfully' : 'Leave request processed')

        // Invalidate queries
        queryClient.invalidateQueries({
          queryKey: getSupervisorPendingRequestsQueryKey(),
        })
        queryClient.invalidateQueries({
          queryKey: getSupervisorApprovedRequestsQueryKey(),
        })
        queryClient.invalidateQueries({
          queryKey: getTrackLeaveApplicationQueryKey(leaveRequestId!),
        })

        setActionNote('')

        onClose()
        onActionComplete?.()
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Failed to process leave request')
      },
    },
  })

  const tracking = trackingQuery.data?.data
  const balanceContext = leaveRequest?.balanceContext
  const currentBalance = balanceContext?.currentBalance
  const startDateLabel = formatOptionalDate(leaveRequest?.startDate ?? tracking?.startDate ?? balanceContext?.startDate)
  const endDateLabel = formatOptionalDate(leaveRequest?.endDate ?? tracking?.endDate ?? balanceContext?.endDate)
  const leaveTypeLabel = leaveRequest?.leaveTypeName ?? tracking?.leaveType ?? balanceContext?.leaveTypeName ?? '—'
  const requestStatusLabel = leaveRequest?.status ?? tracking?.currentStatus
  const daysRequested = balanceContext?.daysRequested ?? leaveRequest?.workingDays ?? tracking?.workingDays
  const balanceWarnings = balanceContext?.warnings ?? []
  const approvalBlockedByBalance = Boolean(balanceContext?.exceedsBalance || balanceContext?.exceedsBorrowingLimit)

  // Determine if current user can approve/reject
  const canTakeAction = useMemo(() => {
    if (!tracking || !user)
      return false

    // Find the current approval step
    const currentStep = tracking.approvalSteps?.find(
      (step: LeaveLifecycleStepDto) => !step.isCompleted && !step.isSkipped,
    )

    if (!currentStep)
      return false

    // Check if current user is the expected approver
    if (currentStep.expectedApproverId !== user.id)
      return false

    // Check if next approver has already acted (constraint)
    const currentStepNumber = currentStep.stepNumber || 0
    const nextSteps = tracking.approvalSteps?.filter(
      (step: LeaveLifecycleStepDto) => (step.stepNumber || 0) > currentStepNumber,
    )

    const nextApproverActed = nextSteps?.some((step: LeaveLifecycleStepDto) => step.isCompleted)

    return !nextApproverActed
  }, [tracking, user])

  let detailsContent: JSX.Element
  if (trackingQuery.isLoading) {
    detailsContent = (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }
  else if (trackingQuery.isError) {
    detailsContent = (
      <Alert variant="destructive" className="mt-4">
        <AlertDescription>
          Failed to load leave request details. Please try again.
        </AlertDescription>
      </Alert>
    )
  }
  else {
    detailsContent = (
      <div className="space-y-6 px-6">
        {/* Leave Request Info */}
        <Label className="text-base">Request Information</Label>

        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Applicant</p>
            <div className="flex items-center gap-2 mt-1">
              <User className="size-4" />
              <p className="font-medium">{tracking?.applicantName}</p>
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Department</p>
            <p className="font-medium mt-1">{leaveRequest?.departmentName ?? tracking?.department ?? '—'}</p>
          </div>
          {' '}
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Leave Type</p>
            <Badge className="mt-1">{leaveTypeLabel}</Badge>
          </div>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Status</p>
            <Badge variant="outline" className="mt-1">
              {requestStatusLabel?.replaceAll('_', ' ') ?? '—'}
            </Badge>
          </div>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Start Date</p>
            <div className="flex items-center gap-2 mt-1">
              <Calendar className="size-4" />
              <p className="font-medium">
                {startDateLabel}
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">End Date</p>
            <div className="flex items-center gap-2 mt-1">
              <Calendar className="size-4" />
              <p className="font-medium">
                {endDateLabel}
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Requested Days</p>
            <p className="font-medium mt-1">
              {formatDaysValue(daysRequested)}
            </p>
          </div>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Days Taken To Date</p>
            <p className="font-medium mt-1">{formatDaysValue(currentBalance?.daysTakenToDate)}</p>
          </div>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Days Remaining</p>
            <p className="font-medium mt-1">{formatDaysValue(currentBalance?.totalRemaining)}</p>
          </div>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Covering Officer</p>
            <p className="font-medium mt-1">{tracking?.coveringUserName ?? 'Not Assigned'}</p>
          </div>
        </div>

        <hr className="my-4 border-t-4 border-b-gray-500" />

        {balanceContext
          ? (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Balance Context</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Annual Remaining</p>
                      <p className="font-medium">{formatDaysValue(currentBalance?.annualRemaining)}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Carryover Remaining</p>
                      <p className="font-medium">{formatDaysValue(currentBalance?.carryoverRemaining)}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Carryover Expiry</p>
                      <p className="font-medium">{formatOptionalDate(currentBalance?.carryoverExpiryDate)}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Projected Remaining After Approval</p>
                      <p className="font-medium">{formatDaysValue(balanceContext.projectedTotalRemaining)}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Projected Effective Balance</p>
                      <p className="font-medium">{formatDaysValue(balanceContext.projectedEffectiveBalance)}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Projected Borrowed Days</p>
                      <p className="font-medium">{formatDaysValue(balanceContext.projectedBorrowedDays)}</p>
                    </div>
                  </div>

                  {balanceContext.triggersBorrowing
                    ? (
                      <Alert>
                        <AlertTitle>Approval will trigger borrowing</AlertTitle>
                        <AlertDescription>
                          This request will consume days from next year entitlement.
                        </AlertDescription>
                      </Alert>
                    )
                    : null}

                  {approvalBlockedByBalance
                    ? (
                      <Alert variant="destructive">
                        <AlertTitle>Insufficient balance for approval</AlertTitle>
                        <AlertDescription>
                          Approving this request exceeds allowed balance or borrowing limits. Backend validation will reject it.
                        </AlertDescription>
                      </Alert>
                    )
                    : null}

                  {balanceWarnings.length > 0
                    ? (
                      <Alert>
                        <AlertTitle>Balance warnings</AlertTitle>
                        <AlertDescription>
                          <ul className="list-disc pl-4">
                            {balanceWarnings.map(warning => (
                              <li key={warning}>{warning}</li>
                            ))}
                          </ul>
                        </AlertDescription>
                      </Alert>
                    )
                    : null}
                </CardContent>
              </Card>
              <hr className="my-4 border-t-4" />
            </>
          )
          : null}

        {/* Approval Progress */}
        {tracking && (
          <div className="space-y-2">
            <Label className="text-base">Approval Progress</Label>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-muted-foreground">Overall Progress</span>
              <span className="font-medium">
                {tracking.progressPercentage}
                %
              </span>
            </div>
            <Progress value={tracking.progressPercentage} />
            <p className="text-xs text-muted-foreground mt-2">
              Level
              {' '}
              {tracking.currentApprovalLevel}
              {' '}
              of
              {' '}
              {tracking.totalApprovalLevels}
            </p>
          </div>
        )}

        {/* Action Section */}
        {canTakeAction && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Take Action</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="actionNote">Comments (Optional)</Label>
                <Textarea
                  id="actionNote"
                  value={actionNote}
                  onChange={e => setActionNote(e.target.value)}
                  placeholder="Add your comments or notes..."
                  className="mt-2"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    )
  }

  const handleAction = (approve: boolean) => {
    if (!leaveRequestId)
      return

    approveOrRejectMutation.mutate({
      id: leaveRequestId,
      data: {
        approve,
        comments: actionNote.trim() || (approve ? 'Approved' : 'Declined'),
      },
    })
  }

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto bg-gradient-to-b from-background to-secondary/5 border-l shadow-xl">
        <SheetHeader className="pb-4 border-b">
          <SheetTitle className="text-2xl font-bold">Leave Request Details</SheetTitle>
          <SheetDescription className="text-base">
            Review leave request information and approval tracking
          </SheetDescription>
        </SheetHeader>

        {detailsContent}

        {canTakeAction && (
          <SheetFooter className="mt-8 flex-col sm:flex-row gap-3 pt-6 border-t">
            <Button
              onClick={() => handleAction(false)}
              disabled={approveOrRejectMutation.isPending}
              variant="destructive"
            // className="w-full sm:w-auto hover:bg-red-600"
            >
              <XCircle className="mr-2 size-4" />
              Decline
            </Button>
            <Button
              onClick={() => handleAction(true)}
              disabled={approveOrRejectMutation.isPending || approvalBlockedByBalance}
              variant="secondary"
            // className="w-full sm:w-auto bg-primary-foreground hover:bg-accent-700 text-white"
            >
              <CheckCircle2 className="mr-2 size-4" />
              Approve
            </Button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  )
}
